import { useState, useMemo, useCallback, useEffect } from 'react';
import { ChatMessage } from '../types/chat.types';

interface UseMessageSearchProps {
  messages: ChatMessage[];
  isEnabled?: boolean;
}

interface UseMessageSearchReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: ChatMessage[];
  totalResults: number;
  currentResultIndex: number;
  currentResultId: string | null;
  navigateNext: () => void;
  navigatePrev: () => void;
  navigateToResult: (index: number) => void;
  clearSearch: () => void;
  highlightedMessageId: string | null;
}

/**
 * Hook para busca e navegação em mensagens
 *
 * @example
 * const {
 *   searchQuery,
 *   setSearchQuery,
 *   searchResults,
 *   totalResults,
 *   currentResultIndex,
 *   navigateNext,
 *   navigatePrev,
 *   highlightedMessageId
 * } = useMessageSearch({ messages, isEnabled: isSearchOpen });
 */
export const useMessageSearch = ({
  messages,
  isEnabled = true,
}: UseMessageSearchProps): UseMessageSearchReturn => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

  // Buscar mensagens que correspondem à query
  const searchResults = useMemo(() => {
    if (!isEnabled || !searchQuery.trim()) {
      return [];
    }

    const query = searchQuery.toLowerCase().trim();

    return messages.filter((message: any) => {
      // Extrair texto do conteúdo (content pode ser string, array ou objeto)
      let searchableText = '';

      if (message.plainText) {
        // Se existe plainText (campo extraído do backend), usar direto
        searchableText = message.plainText;
      } else if (typeof message.content === 'string') {
        // Se content é string simples
        searchableText = message.content;
      } else if (Array.isArray(message.content)) {
        // Se content é array de objetos (estrutura do backend)
        searchableText = message.content
          .map((item: any) => {
            if (item.type === 'text') return item.text || '';
            if (item.type === 'image' || item.type === 'video') return item.caption || '[Imagem]';
            if (item.type === 'audio') return '[Áudio]';
            if (item.type === 'document') return item.filename || '[Documento]';
            if (item.type === 'location') return item.name || item.address || '[Localização]';
            if (item.type === 'contact') return item.name || '[Contato]';
            return '';
          })
          .join(' ');
      } else if (typeof message.content === 'object' && message.content?.type) {
        // Se content é objeto único (estrutura alternativa)
        const item = message.content;
        if (item.type === 'text') searchableText = item.text || '';
        if (item.type === 'image' || item.type === 'video') searchableText = item.caption || '[Imagem]';
        if (item.type === 'audio') searchableText = '[Áudio]';
        if (item.type === 'document') searchableText = item.filename || '[Documento]';
        if (item.type === 'location') searchableText = item.name || item.address || '[Localização]';
        if (item.type === 'contact') searchableText = item.name || '[Contato]';
      }

      const contentMatch = searchableText.toLowerCase().includes(query);

      // Buscar no nome do remetente (se disponível)
      const senderMatch = typeof message.sender === 'object' && message.sender?.name?.toLowerCase().includes(query);

      // Buscar em metadados de mídia (estrutura legada)
      const mediaMatch = message.media?.fileName?.toLowerCase().includes(query);

      return contentMatch || senderMatch || mediaMatch;
    });
  }, [messages, searchQuery, isEnabled]);

  const totalResults = searchResults.length;

  // ID da mensagem atual destacada
  const currentResultId = useMemo(() => {
    if (searchResults.length === 0) return null;
    return searchResults[currentResultIndex]?.id || null;
  }, [searchResults, currentResultIndex]);

  // Navegar para próximo resultado
  const navigateNext = useCallback(() => {
    if (totalResults === 0) return;

    setCurrentResultIndex((prev) => {
      const next = prev + 1;
      return next >= totalResults ? 0 : next; // Loop para o início
    });
  }, [totalResults]);

  // Navegar para resultado anterior
  const navigatePrev = useCallback(() => {
    if (totalResults === 0) return;

    setCurrentResultIndex((prev) => {
      const next = prev - 1;
      return next < 0 ? totalResults - 1 : next; // Loop para o fim
    });
  }, [totalResults]);

  // Navegar para resultado específico
  const navigateToResult = useCallback(
    (index: number) => {
      if (index < 0 || index >= totalResults) return;
      setCurrentResultIndex(index);
    },
    [totalResults]
  );

  // Limpar busca
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setCurrentResultIndex(0);
    setHighlightedMessageId(null);
  }, []);

  // Atualizar highlightedMessageId quando currentResultId mudar
  useEffect(() => {
    setHighlightedMessageId(currentResultId);
  }, [currentResultId]);

  // Reset index quando query mudar
  useEffect(() => {
    setCurrentResultIndex(0);
  }, [searchQuery]);

  // Reset tudo quando desabilitado
  useEffect(() => {
    if (!isEnabled) {
      setCurrentResultIndex(0);
      setHighlightedMessageId(null);
    }
  }, [isEnabled]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    totalResults,
    currentResultIndex,
    currentResultId,
    navigateNext,
    navigatePrev,
    navigateToResult,
    clearSearch,
    highlightedMessageId,
  };
};
